import { useState, useRef } from "react";
import { API } from "../../global/global";

interface CameraInputProps {
  onCapture: (file: File) => void;
  isDarkMode: boolean;
  evaluadoUuid: string;
  nombreCompleto: string;
}

// Función exportada - movida fuera del componente
export const sendPhotoToBackend = async (
  file: File,
  evaluadoUuid: string,
  nombreCompleto: string
) => {
  const formData = new FormData();
  formData.append("photo", file);
  formData.append("nombreCompleto", nombreCompleto);

  const token = localStorage.getItem("token");

  try {
    const response = await fetch(`${API}/upload-photo/${evaluadoUuid}`, {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      console.log("Imagen subida exitosamente");
      return true;
    } else {
      console.error("Error al subir la imagen", response.statusText);
      return false;
    }
  } catch (error) {
    console.error("Error en la petición", error);
    return false;
  }
};

const CameraInput: React.FC<CameraInputProps> = ({
  onCapture,
  isDarkMode,
  evaluadoUuid,
  nombreCompleto,
}) => {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const openCamera = () => {
    setIsCameraOpen(true);
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          mediaStreamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((err) => {
          console.error("Error al acceder a la cámara", err);
        });
    }
  };

  const capturePhoto = () => {
    if (canvasRef.current && videoRef.current) {
      const context = canvasRef.current.getContext("2d");
      if (context) {
        context.drawImage(
          videoRef.current,
          0,
          0,
          canvasRef.current.width,
          canvasRef.current.height
        );
        canvasRef.current.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], "captured_photo.png", {
              type: "image/png",
            });
            onCapture(file);
            // Llamada corregida con los tres parámetros
            sendPhotoToBackend(file, evaluadoUuid, nombreCompleto);
            closeCamera();
          }
        });
      }
    }
  };

  const closeCamera = () => {
    setIsCameraOpen(false);
    if (mediaStreamRef.current) {
      const tracks = mediaStreamRef.current.getTracks();
      tracks.forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  return (
    <div className="space-y-2">
      <label
        className={`block text-sm font-medium ${
          isDarkMode ? "text-white" : "text-gray-700"
        }`}
      ></label>
      {isCameraOpen ? (
        <div className="space-y-4">
          {/* Contenedor principal con video y mensaje "Sonríe" */}
          <div className="flex items-start gap-4">
            {/* Contenedor del video (se mantiene igual) */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl border-4 border-white/20 hover:border-white/40 transition-all duration-300 flex-1">
              <video
                ref={videoRef}
                autoPlay
                className="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-500"
              />

              {/* Botón REC parpadeante */}
              <div className="absolute top-3 left-3 flex items-center">
                <span className="relative flex h-4 w-4">
                  <span
                    className={`animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 ${
                      isCameraOpen ? "block" : "hidden"
                    }`}
                  ></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600"></span>
                </span>
                <span className="ml-1 text-xs font-bold text-white">REC</span>
              </div>

              {/* Efecto de marco futurista */}
              <div className="absolute inset-0 border-8 border-transparent hover:border-white/10 rounded-xl pointer-events-none transition-all duration-500" />
              {/* Efecto de esquinas */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white/50" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white/50" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white/50" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white/50" />
            </div>

            {/* Mensaje "Sonríe" con animaciones */}
            <div className="hidden md:flex flex-col items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl p-4 w-32 h-64 shadow-lg animate-bounce-in">
              <div className="text-5xl animate-wiggle mb-2">😊</div>
              <div className="text-center">
                <span className="text-white font-bold text-lg block animate-pulse">
                  ¡Sonríe!
                </span>
                <span className="text-white text-xs block mt-1 animate-fade-in">
                  Estás en cámara
                </span>
              </div>
              <div className="absolute bottom-2 right-2 left-2 h-1 bg-white/30 rounded-full overflow-hidden">
                <div className="h-full bg-white/80 rounded-full animate-progress"></div>
              </div>
            </div>
          </div>

          {/* Botones de captura (se mantienen igual) */}
          <div className="flex space-x-4 justify-center">
            <button
              type="button"
              onClick={capturePhoto}
              className="bg-gradient-to-r from-green-400 to-green-600 text-white py-2 px-6 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Capturar
            </button>
            <button
              onClick={closeCamera}
              className="bg-gradient-to-r from-red-400 to-red-600 text-white py-2 px-6 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Cerrar
            </button>
          </div>
          <canvas
            ref={canvasRef}
            width="640"
            height="480"
            style={{ display: "none" }}
          />
        </div>
      ) : (
        <button
          onClick={openCamera}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white py-3 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center mx-auto"
        >
          <svg
            className="w-6 h-6 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          Abrir Cámara
        </button>
      )}
    </div>
  );
};

export default CameraInput;
